/**
 * helper-engines — Phase 25 hybrid local execution.
 *
 * The wizard engines (loudness/stabilize/bgm-duck/voice-change/slow-mo/
 * gif-encode/bg-remove/noise-reduce/upscale) should run on the user's own
 * machine when the Helper EXE is installed — this is the entire point of the
 * hybrid architecture. The browser has the file; the Helper has ffmpeg +
 * Python + (when available) the user's GPU. Server-side fallback only fires
 * for users without Helper.
 *
 * This module implements the local path: write the input blob to a Helper
 * work folder, run /ffmpeg/exec (or /python/exec for the ML wizards) on the
 * resulting local file, then mint a /files/read token so the user can
 * download the processed result back from the same local Helper.
 */

import {
	HELPER_DEFAULT_PORT,
	STUDIO_ORIGIN,
	type InfoResponse,
} from "@makemonlab/helper-client";
import { helperPing, helperInfo } from "./helper-client";

const BASE = `http://localhost:${HELPER_DEFAULT_PORT}`;

export interface HelperEngineResult {
	ok: boolean;
	outputPath?: string;
	downloadUrl?: string;
	engine?: string;
	error?: string;
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
	const bytes = new Uint8Array(buf);
	const CHUNK = 0x8000;
	let binary = "";
	for (let i = 0; i < bytes.length; i += CHUNK) {
		binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
	}
	return btoa(binary);
}

function uuid(): string {
	return (
		typeof crypto !== "undefined" && crypto.randomUUID
			? crypto.randomUUID()
			: Math.random().toString(36).slice(2) + Date.now().toString(36)
	).replace(/-/g, "");
}

function joinPath(workFolder: string, ...parts: string[]): string {
	const sep = workFolder.includes("\\") ? "\\" : "/";
	return [workFolder, ...parts].join(sep);
}

async function getWorkFolder(): Promise<string | null> {
	const info: InfoResponse | null = await helperInfo();
	return info?.storage?.[0]?.path ?? null;
}

/** Write a Blob/File to a path under the Helper work folder. Returns the absolute local path on success. */
async function writeToHelper(
	workFolder: string,
	relPath: string,
	buffer: ArrayBuffer,
): Promise<string | null> {
	const targetPath = joinPath(workFolder, ...relPath.split("/"));
	const r = await fetch(`${BASE}/files/write`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Origin: STUDIO_ORIGIN },
		body: JSON.stringify({
			path: targetPath,
			content_base64: arrayBufferToBase64(buffer),
			mode: "overwrite",
		}),
	});
	if (!r.ok) return null;
	return targetPath;
}

/** Mint a same-origin Helper stream URL for the given local file path. */
async function helperDownloadUrlFor(localPath: string): Promise<string | null> {
	const r = await fetch(`${BASE}/files/read`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Origin: STUDIO_ORIGIN },
		body: JSON.stringify({ path: localPath }),
	});
	if (!r.ok) return null;
	const j = (await r.json().catch(() => ({}))) as {
		stream_url?: string;
		content_base64?: string;
	};
	if (j.stream_url) return `${BASE}${j.stream_url}`;
	// Tiny files come back inline; convert to a blob URL.
	if (j.content_base64) {
		const bin = atob(j.content_base64);
		const u8 = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i += 1) u8[i] = bin.charCodeAt(i);
		return URL.createObjectURL(new Blob([u8]));
	}
	return null;
}

async function runFfmpegExec(args: string[]): Promise<{ exit: number; stderr_tail: string }> {
	const r = await fetch(`${BASE}/ffmpeg/exec`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Origin: STUDIO_ORIGIN },
		body: JSON.stringify({ args }),
	});
	if (!r.ok) {
		const txt = await r.text().catch(() => "");
		return { exit: -1, stderr_tail: txt };
	}
	return (await r.json()) as { exit: number; stderr_tail: string };
}

/** Run an allowlisted Python script via /python/exec (SSE) and resolve when "exit" event fires. */
async function runPythonExec(
	script: string,
	args: Record<string, unknown>,
	onProgress?: (pct: number) => void,
): Promise<{ outputPath?: string; engine?: string; error?: string }> {
	const r = await fetch(`${BASE}/python/exec`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Origin: STUDIO_ORIGIN },
		body: JSON.stringify({ script, args }),
	});
	if (!r.ok || !r.body) {
		const txt = await r.text().catch(() => "");
		return { error: txt || `python_exec_failed_${r.status}` };
	}
	const reader = r.body.getReader();
	const dec = new TextDecoder();
	let buf = "";
	let result: { outputPath?: string; engine?: string; error?: string } = {};
	while (true) {
		const { value, done } = await reader.read();
		if (done) break;
		buf += dec.decode(value, { stream: true });
		const events = buf.split("\n\n");
		buf = events.pop() ?? "";
		for (const ev of events) {
			const dataLine = ev.split("\n").find((l) => l.startsWith("data:"));
			if (!dataLine) continue;
			try {
				const obj = JSON.parse(dataLine.slice(5).trim()) as {
					type?: string;
					percent?: number;
					output_path?: string;
					engine?: string;
					error?: string;
				};
				if (obj.type === "progress" && typeof obj.percent === "number") {
					onProgress?.(obj.percent);
				}
				if (obj.type === "result") {
					result = {
						outputPath: obj.output_path,
						engine: obj.engine,
						error: obj.error,
					};
				}
			} catch {
				// ignore malformed lines
			}
		}
	}
	return result;
}

interface WizardJob {
	wizardKey: string;
	file: File;
	bgmFile?: File;
}

/** Map a wizard key to its output extension. */
function outExtFor(wizardKey: string, mode?: string): string {
	if (wizardKey === "AI-33" || wizardKey === "AI-36" || wizardKey === "AI-18")
		return ".m4a";
	if (wizardKey === "AI-32") return mode === "transparent" ? ".webm" : ".mp4";
	return ".mp4";
}

function ffmpegArgsForWizard(
	wizardKey: string,
	input: string,
	output: string,
	bgm?: string,
): string[] | null {
	switch (wizardKey) {
		case "AI-41": // loudness-normalize (single-pass; honest fallback)
			return [
				"-y", "-hide_banner", "-i", input,
				"-af", "loudnorm=I=-14:TP=-1.5:LRA=11",
				"-c:v", "copy", "-c:a", "aac", "-b:a", "192k", output,
			];
		case "AI-34": // stabilize (single-pass vidstab — picks default smoothing)
			return [
				"-y", "-hide_banner", "-i", input,
				"-vf",
				"vidstabdetect=shakiness=5:accuracy=15:result=transforms.trf,vidstabtransform=smoothing=10:crop=black,unsharp=5:5:0.8:3:3:0.4",
				"-c:v", "libx264", "-preset", "medium", "-crf", "18",
				"-c:a", "copy", output,
			];
		case "AI-18": // bgm-duck — voice + bgm
			if (!bgm) return null;
			return [
				"-y", "-hide_banner", "-i", input, "-i", bgm,
				"-filter_complex",
				"[0:a]asplit=2[vkey][vmix];[1:a][vkey]sidechaincompress=threshold=0.03:ratio=8:attack=5:release=250[bg];[vmix][bg]amix=inputs=2:duration=longest:dropout_transition=0[aout]",
				"-map", "[aout]", "-c:a", "aac", "-b:a", "192k", output,
			];
		case "AI-36": // voice-change (robot preset by default — full UI binding can override)
			return [
				"-y", "-hide_banner", "-i", input,
				"-af",
				"asetrate=44100,atempo=1.0,aresample=44100,aphaser=type=t:speed=0.5,vibrato=f=8:d=0.4",
				"-c:a", "aac", "-b:a", "192k", output,
			];
		case "AI-42": // slow-mo (factor 2x default, no interpolation for speed)
			return [
				"-y", "-hide_banner", "-i", input,
				"-filter_complex",
				"[0:v]setpts=2.0*PTS[v];[0:a]atempo=0.5[a]",
				"-map", "[v]", "-map", "[a]",
				"-c:v", "libx264", "-preset", "medium", "-crf", "18",
				"-c:a", "aac", "-b:a", "192k", output,
			];
		default:
			return null;
	}
}

/**
 * Run a wizard locally via the Helper. Falls through (returns ok:false with
 * engine:"helper_unavailable") so callers can use server fallback.
 */
export async function helperRunEngineWizard(
	job: WizardJob,
	onProgress?: (pct: number) => void,
): Promise<HelperEngineResult> {
	const ping = await helperPing();
	if (!ping) return { ok: false, error: "helper_unavailable" };
	const work = await getWorkFolder();
	if (!work) return { ok: false, error: "helper_no_workspace" };

	const id = uuid().slice(0, 12);
	const inExt = (job.file.name.match(/\.[^.]+$/)?.[0] ?? ".bin").toLowerCase();
	const inPath = await writeToHelper(
		work,
		`wizard-in/${id}${inExt}`,
		await job.file.arrayBuffer(),
	);
	if (!inPath) return { ok: false, error: "helper_write_input_failed" };

	let bgmPath: string | null = null;
	if (job.bgmFile) {
		const bgmExt = (job.bgmFile.name.match(/\.[^.]+$/)?.[0] ?? ".m4a").toLowerCase();
		bgmPath = await writeToHelper(
			work,
			`wizard-in/${id}_bgm${bgmExt}`,
			await job.bgmFile.arrayBuffer(),
		);
		if (!bgmPath) return { ok: false, error: "helper_write_bgm_failed" };
	}

	const outName = `${id}_${job.wizardKey}${outExtFor(job.wizardKey)}`;
	const outPath = joinPath(work, "wizard-out", outName);

	// ML wizards via Python.
	if (job.wizardKey === "AI-32") {
		// bg-remove
		const py = await runPythonExec(
			"bg_remove",
			{
				input: inPath,
				output_dir: joinPath(work, "wizard-out"),
				mode: "transparent",
			},
			onProgress,
		);
		if (py.error || !py.outputPath) {
			return { ok: false, error: py.error ?? "bg_remove_failed", engine: "fallback" };
		}
		const dl = await helperDownloadUrlFor(py.outputPath);
		return { ok: true, outputPath: py.outputPath, downloadUrl: dl ?? undefined, engine: py.engine };
	}
	if (job.wizardKey === "AI-33") {
		// noise-reduce
		const py = await runPythonExec(
			"noise_reduce",
			{
				input: inPath,
				output_dir: joinPath(work, "wizard-out"),
				strength: 0.5,
			},
			onProgress,
		);
		if (py.error || !py.outputPath) {
			return { ok: false, error: py.error ?? "noise_reduce_failed", engine: "fallback" };
		}
		const dl = await helperDownloadUrlFor(py.outputPath);
		return { ok: true, outputPath: py.outputPath, downloadUrl: dl ?? undefined, engine: py.engine };
	}
	if (job.wizardKey === "AI-43") {
		// upscale
		const py = await runPythonExec(
			"upscale",
			{
				input: inPath,
				output_dir: joinPath(work, "wizard-out"),
				target: "1080p",
				scale: 2,
			},
			onProgress,
		);
		if (py.error || !py.outputPath) {
			return { ok: false, error: py.error ?? "upscale_failed", engine: "fallback" };
		}
		const dl = await helperDownloadUrlFor(py.outputPath);
		return { ok: true, outputPath: py.outputPath, downloadUrl: dl ?? undefined, engine: py.engine };
	}

	// ffmpeg-only wizards.
	const args = ffmpegArgsForWizard(job.wizardKey, inPath, outPath, bgmPath ?? undefined);
	if (!args) {
		return { ok: false, error: "helper_wizard_not_supported" };
	}
	// Ensure output dir exists via a no-op write (path validator + mkdir behavior).
	await fetch(`${BASE}/files/write`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Origin: STUDIO_ORIGIN },
		body: JSON.stringify({
			path: outPath + ".placeholder",
			content_base64: "",
			mode: "overwrite",
		}),
	}).catch(() => null);
	const r = await runFfmpegExec(args);
	if (r.exit !== 0) {
		return { ok: false, error: r.stderr_tail || `ffmpeg_exit_${r.exit}`, engine: "fallback" };
	}
	const dl = await helperDownloadUrlFor(outPath);
	return {
		ok: true,
		outputPath: outPath,
		downloadUrl: dl ?? undefined,
		engine: `helper_ffmpeg_${job.wizardKey}`,
	};
}

/**
 * Edge TTS narration synthesis via the local Helper (D-2205 helper-first).
 * Returns a same-origin Helper download URL for the resulting mp3.
 */
export async function helperSynthesizeNarration(opts: {
	text: string;
	voiceId: string; // e.g. "jiu", "minjun" — mapped to an edge_tts voice
	language?: "ko" | "en" | "ja" | "zh";
	speed?: number;
	pitch?: number;
	tone?: string | null;
}): Promise<HelperEngineResult> {
	const ping = await helperPing();
	if (!ping) return { ok: false, error: "helper_unavailable" };
	const work = await getWorkFolder();
	if (!work) return { ok: false, error: "helper_no_workspace" };

	// Voice mapping mirrors core/app/engines/youtube_shorts/tts_synthesizer.DEFAULT_VOICES.
	const voiceMap: Record<string, string> = {
		minjun: "ko-KR-InJoonNeural",
		jiu: "ko-KR-SunHiNeural",
		hyejin: "ko-KR-JiMinNeural",
		taeyang: "ko-KR-GookMinNeural",
		sujin: "ko-KR-YuJinNeural",
	};
	const langDefaults: Record<string, string> = {
		ko: "ko-KR-SunHiNeural",
		en: "en-US-AriaNeural",
		ja: "ja-JP-NanamiNeural",
		zh: "zh-CN-XiaoxiaoNeural",
	};
	const voice =
		voiceMap[opts.voiceId] ??
		langDefaults[opts.language ?? "ko"] ??
		"ko-KR-SunHiNeural";

	// Map tone -> edge_tts rate string (matches core fallback heuristic).
	let rate = "+0%";
	if (opts.tone && ["excited", "thrilled", "angry"].includes(opts.tone)) rate = "+15%";
	else if (opts.tone && ["calm", "gentle", "sad"].includes(opts.tone)) rate = "-10%";

	const py = await runPythonExec("narration_tts", {
		text: opts.text,
		voice,
		output_dir: joinPath(work, "narration-out"),
		rate,
		pitch: "+0Hz",
		speed: opts.speed ?? 1.0,
		pitch_semitones: opts.pitch ?? 0,
	});
	if (py.error || !py.outputPath) {
		return { ok: false, error: py.error ?? "narration_tts_failed" };
	}
	// narration_tts.py emits both `audio_path` and `output_path` for symmetry
	// with the other Helper ML scripts (which use `output_path`).
	const localPath = py.outputPath;
	const dl = await helperDownloadUrlFor(localPath);
	return {
		ok: true,
		outputPath: localPath,
		downloadUrl: dl ?? undefined,
		engine: py.engine ?? "edge_tts_local",
	};
}

/**
 * GIF export via the local Helper. Same idea as helperRunEngineWizard but the
 * ffmpeg recipe is the palettegen+paletteuse 2-pass for high-quality output.
 */
export async function helperEncodeGif(opts: {
	file: File;
	fps?: number;
	width?: number;
	durationSec?: number;
}): Promise<HelperEngineResult> {
	const ping = await helperPing();
	if (!ping) return { ok: false, error: "helper_unavailable" };
	const work = await getWorkFolder();
	if (!work) return { ok: false, error: "helper_no_workspace" };

	const fps = Math.max(2, Math.min(30, opts.fps ?? 12));
	const width = Math.max(80, Math.min(1920, opts.width ?? 480));
	const dur = Math.max(0.1, opts.durationSec ?? 1.0);

	const id = uuid().slice(0, 12);
	const inExt = (opts.file.name.match(/\.[^.]+$/)?.[0] ?? ".mp4").toLowerCase();
	const inPath = await writeToHelper(
		work,
		`wizard-in/${id}${inExt}`,
		await opts.file.arrayBuffer(),
	);
	if (!inPath) return { ok: false, error: "helper_write_input_failed" };

	const palettePath = joinPath(work, "wizard-out", `${id}_palette.png`);
	const outPath = joinPath(work, "wizard-out", `${id}.gif`);

	// Pass 1 — palette.
	const p1 = await runFfmpegExec([
		"-y", "-hide_banner", "-t", String(dur), "-i", inPath,
		"-vf", `fps=${fps},scale=${width}:-1:flags=lanczos,palettegen=stats_mode=diff`,
		palettePath,
	]);
	if (p1.exit !== 0) {
		return { ok: false, error: p1.stderr_tail || "palettegen_failed" };
	}
	// Pass 2 — paletteuse.
	const p2 = await runFfmpegExec([
		"-y", "-hide_banner", "-t", String(dur), "-i", inPath, "-i", palettePath,
		"-lavfi",
		`fps=${fps},scale=${width}:-1:flags=lanczos[x];[x][1:v]paletteuse=dither=sierra2_4a:diff_mode=rectangle`,
		"-loop", "0", outPath,
	]);
	if (p2.exit !== 0) {
		return { ok: false, error: p2.stderr_tail || "paletteuse_failed" };
	}
	const dl = await helperDownloadUrlFor(outPath);
	return {
		ok: true,
		outputPath: outPath,
		downloadUrl: dl ?? undefined,
		engine: "helper_ffmpeg_palettegen_paletteuse",
	};
}
